/* STROMA LP runtime. Loaded (deferred) only on template page.stroma.
   No dependencies. Modules attach on DOMContentLoaded; each module no-ops
   cleanly when its section is absent so section reordering can't throw. */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Shared body scroll lock for popups. Pinned-body pattern because
     html{overflow:hidden} collapses scrollY to 0 in Chrome, losing the
     restore point. Owner-counted: several popup modules (shop AXIS, shop
     ZERO, grid) share one <body>, so only the first lock captures the
     restore point and only the last unlock releases and restores it —
     re-entrant calls can't clobber savedScroll with the pinned-state 0.
     Restore uses behavior:'instant' so a theme-level
     scroll-behavior:smooth can't animate the jump back. */
  var scrollLock = (function () {
    var owners = 0;
    var savedScroll = 0;
    return {
      lock: function () {
        owners++;
        if (owners > 1) return;
        savedScroll = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = -savedScroll + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
      },
      unlock: function () {
        if (owners === 0) return;
        owners--;
        if (owners > 0) return;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' });
      },
      isLocked: function () { return owners > 0; }
    };
  })();

  /* Scroll-driven steps counter in the ticker ("207 STEPS" climbs with scroll
     depth). Value = px scrolled mapped so the page bottom lands near the
     design's 14,000-steps motif; live value tuned in verify. */
  function initSteps() {
    var el = document.querySelector('[data-st-steps]');
    if (!el) return;
    // The counter climbs with how far you scroll: every scroll movement (up or
    // down) adds distance, so it always increases and never stalls — even when
    // re-scrolling an area already seen. It eases toward its target so the climb
    // is smooth instead of snapping on each scroll tick.
    var FLOOR = 207;
    var PER_PX = 0.4; // steps added per pixel scrolled — low so the count climbs slowly
    var target = FLOOR; // accumulates scroll distance; only ever grows
    var shown = FLOOR; // animated display value
    var lastY = window.scrollY || 0;
    var raf = null;
    function onScroll() {
      var y = window.scrollY;
      var d = Math.abs(y - lastY);
      lastY = y;
      // Ignore the large snap when a popup locks/unlocks the body (scrollY flips
      // to 0 and back); a real per-frame scroll delta is small.
      if (d > 0 && d < window.innerHeight) target += d * PER_PX;
      kick();
    }
    function tick() {
      var diff = target - shown;
      if (diff > 0.5) {
        shown += Math.max(1, diff * 0.05);
        if (shown > target) shown = target;
        el.textContent = String(Math.round(shown));
        raf = window.requestAnimationFrame(tick);
      } else {
        shown = target;
        el.textContent = String(Math.round(shown));
        raf = null;
      }
    }
    function kick() { if (!raf) raf = window.requestAnimationFrame(tick); }
    window.addEventListener('scroll', onScroll, { passive: true });
    el.textContent = String(Math.round(shown));
  }

  /* Content-aware crop positioning. Product boxes keep the design's exact
     geometry with object-fit:cover, but the store's uploads are 4:5 with
     subjects at varying heights — any fixed crop anchor cuts either
     soles/shadows (center) or heads/collars (bottom) on some image. Each
     registered image is measured once on a downscaled canvas (subject =
     pixels deviating from the studio backdrop sampled at the frame edges),
     then its crop window is placed so the subject stays whole. When the
     subject is larger than the window (tight detail shots), the window pins
     to the subject's top with slight headroom — faces and collars always
     survive; only bottom edge pixels trim. Re-evaluated on resize (box
     aspect changes across breakpoints). CDN images are same-origin, so the
     canvas stays readable; a tainted canvas or failed decode leaves the
     stylesheet's static anchor in place. */
  var cropReg = [];
  var subjectCache = {};

  function measureSubject(img) {
    var key = (img.currentSrc || img.src).replace(/([?&])width=\d+/, '$1');
    if (Object.prototype.hasOwnProperty.call(subjectCache, key)) return subjectCache[key];
    var sub = null;
    try {
      var W = 48;
      var H = Math.max(8, Math.round(W * img.naturalHeight / img.naturalWidth));
      var cv = document.createElement('canvas');
      cv.width = W;
      cv.height = H;
      var cx = cv.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0, W, H);
      var d = cx.getImageData(0, 0, W, H).data;
      var x0 = W, x1 = -1, y0 = H, y1 = -1;
      for (var y = 0; y < H; y++) {
        var li = (y * W) * 4;
        var ri = (y * W + W - 1) * 4;
        var br = (d[li] + d[ri]) / 2;
        var bg = (d[li + 1] + d[ri + 1]) / 2;
        var bb = (d[li + 2] + d[ri + 2]) / 2;
        for (var x = 0; x < W; x++) {
          var i = (y * W + x) * 4;
          if (Math.abs(d[i] - br) + Math.abs(d[i + 1] - bg) + Math.abs(d[i + 2] - bb) > 78) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 >= 0) sub = { x0: x0 / W, x1: (x1 + 1) / W, y0: y0 / H, y1: (y1 + 1) / H };
    } catch (e) { /* keep the stylesheet's static anchor */ }
    subjectCache[key] = sub;
    return sub;
  }

  function placeCrop(entry) {
    var img = entry.img;
    /* JS-inserted images are kept invisible until their crop is placed —
       otherwise they first paint at the default centered position and
       visibly jump when the subject-aware objectPosition lands. Every
       exit path below must reveal. */
    function reveal() {
      if (img.style.opacity === '0') {
        img.style.transition = 'opacity .15s ease';
        img.style.opacity = '1';
      }
    }
    if (!img.isConnected || !img.naturalWidth) { reveal(); return; }
    var r = img.getBoundingClientRect();
    if (!r.width || !r.height) { reveal(); return; }
    var boxAR = r.width / r.height;
    var imgAR = img.naturalWidth / img.naturalHeight;
    if (Math.abs(imgAR - boxAR) < 0.01) { reveal(); return; }
    var sub = measureSubject(img);
    if (!sub) { reveal(); return; }
    if (imgAR < boxAR) {
      var win = imgAR / boxAR;
      var t;
      if (sub.y1 - sub.y0 <= win) {
        t = (Math.max(0, sub.y1 - win) + Math.min(sub.y0, 1 - win)) / 2;
      } else {
        t = Math.min(Math.max(0, sub.y0 - 0.03), 1 - win);
      }
      var denom = 1 - win;
      img.style.objectPosition = '50% ' + (denom > 0.001 ? (t / denom * 100).toFixed(1) : '50') + '%';
    } else {
      var winW = boxAR / imgAR;
      var l;
      if (sub.x1 - sub.x0 <= winW) {
        l = (Math.max(0, sub.x1 - winW) + Math.min(sub.x0, 1 - winW)) / 2;
      } else {
        l = Math.min(Math.max(0, (sub.x0 + sub.x1) / 2 - winW / 2), 1 - winW);
      }
      var denomW = 1 - winW;
      img.style.objectPosition = (denomW > 0.001 ? (l / denomW * 100).toFixed(1) : '50') + '% 50%';
    }
    reveal();
  }

  function smartCrop(img) {
    if (!img) return;
    var entry = { img: img };
    cropReg = cropReg.filter(function (e) { return e.img.isConnected; });
    cropReg.push(entry);
    if (img.complete && img.naturalWidth) {
      placeCrop(entry);
    } else {
      img.style.opacity = '0';
      img.addEventListener('load', function () { placeCrop(entry); }, { once: true });
      img.addEventListener('error', function () { img.style.opacity = ''; }, { once: true });
    }
  }

  var cropResizeQueued = false;
  window.addEventListener('resize', function () {
    if (cropResizeQueued) return;
    cropResizeQueued = true;
    window.setTimeout(function () {
      cropResizeQueued = false;
      cropReg = cropReg.filter(function (e) { return e.img.isConnected; });
      cropReg.forEach(placeCrop);
    }, 150);
  });

  /* Placeholder registry — populated by later build phases:
     shop modules, notify/download popups, itinerary flip + autoplay,
     features auto-slider, FAQ accordion, anchor scrolls. */
  var modules = [initSteps];
  var started = false;

  function runAll() {
    if (started) return;
    started = true;
    modules.forEach(function (m) {
      try { m(); } catch (e) { console.error('[stroma]', e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    // Deferred script parts below still register synchronously before this
    // microtask-less tail runs on already-parsed documents.
    window.setTimeout(runAll, 0);
  }

  window.__stroma = {
    reducedMotion: REDUCED,
    scrollLock: scrollLock,
    smartCrop: smartCrop,
    register: function (fn) {
      modules.push(fn);
      if (started) { try { fn(); } catch (e) { console.error('[stroma]', e); } }
    }
  };
})();

/* ==== 10-hero.js ==== */
(function(){ 'use strict';
/* STROMA hero: anchor smooth-scrolls (offset by the fixed ticker) and
   reduced-motion video handling. The steps counter is owned by the shared
   runtime via [data-st-steps] — nothing here touches scroll. */
function initStromaHero() {
  var section = document.querySelector('[data-st-hero]');
  if (!section) return;

  var ticker = section.querySelector('[data-st-hero-ticker]');

  // Document-wide: the bench panels live in their own section but share the
  // hero's scroll-link and video attributes.
  document.querySelectorAll('[data-st-hero-scroll]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      // data-st-scroll-target is a CSS selector for a target that has no id of
      // its own (e.g. the landing waitlist button); otherwise the href #id is
      // the target. Either way the href remains a valid no-JS anchor fallback.
      var sel = link.getAttribute('data-st-scroll-target');
      var target = null;
      if (sel) {
        target = document.querySelector(sel);
      } else {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        target = document.getElementById(href.slice(1));
      }
      if (!target) {
        console.warn('[stroma] hero scroll target missing:', sel || link.getAttribute('href'));
        return;
      }
      e.preventDefault();
      var offset = ticker ? ticker.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: top,
        behavior: window.__stroma.reducedMotion ? 'auto' : 'smooth'
      });
    });
  });

  /* Hero CTA: fire the STROMA launch lead-capture inline instead of scrolling.
     leadCapture owns the network + 401 in-page login; sharing the lead tag with
     the landing signup means its DONE key and the lead-resumed event keep the two
     surfaces in sync — registering on one shows "on the list" on both. */
  var heroNotify = document.querySelector('[data-st-hero-notify]');
  if (heroNotify && window.__stroma && window.__stroma.leadCapture) {
    var heroTag = heroNotify.getAttribute('data-lead-tag') || 'notifyme-stromaLaunch';
    var heroLabel = heroNotify.querySelector('.st-label');
    var heroNote = document.querySelector('[data-st-hero-note]');
    var heroCustId = (heroNotify.getAttribute('data-customer-id') || '').trim();
    var heroDoneKey = 'stroma:lead:done:' + heroTag;
    var heroBusy = false;
    var heroSay = function (t) {
      // Suppress the 401 login-prompt line — the login UI is itself the prompt,
      // so echoing it only leaves text lingering after the shopper dismisses login.
      if (t && t.lastIndexOf('Log in to continue', 0) === 0) t = '';
      if (heroNote) { heroNote.textContent = t || ''; heroNote.hidden = !t; }
    };
    var heroMarkDone = function () {
      if (heroLabel) heroLabel.textContent = "You're on the list";
      heroNotify.classList.add('is-done');
      heroNotify.setAttribute('aria-disabled', 'true');
      heroSay('');
      if (heroCustId) { try { localStorage.setItem(heroDoneKey, heroCustId); } catch (err) { /* storage blocked */ } }
    };
    heroNotify.addEventListener('click', function (e) {
      e.preventDefault();
      if (heroBusy || heroNotify.classList.contains('is-done')) return;
      heroBusy = true;
      heroSay('One moment…');
      window.__stroma.leadCapture(heroTag, heroSay).then(function (ok) {
        heroBusy = false;
        if (ok) heroMarkDone();
      });
    });
    document.addEventListener('stroma:lead-resumed', function (e) {
      if (e.detail && e.detail.tag === heroTag) heroMarkDone();
    });
    document.addEventListener('stroma:lead-resume-failed', function (e) {
      if (e.detail && e.detail.tag === heroTag && !heroNotify.classList.contains('is-done')) heroSay(e.detail.message);
    });
    try { if (heroCustId && localStorage.getItem(heroDoneKey) === heroCustId) heroMarkDone(); } catch (err) { /* storage blocked: shopper retaps */ }
  }

  /* Reduced motion: stop hero/bench videos from autoplaying (the CSS
     marquee is paused by the stylesheet). Manual play stays possible. */
  if (window.__stroma.reducedMotion) {
    document.querySelectorAll('video[data-st-hero-video]').forEach(function (v) {
      v.removeAttribute('autoplay');
      v.pause();
    });
  }
}
window.__stroma.register(initStromaHero);
})();

/* ==== 25-leads.js ==== */
(function(){ 'use strict';
/* Lead capture (lotto-commerce-backend /leads App Proxy): tags the logged-in
   shopper's customer profile with an allowlisted tag. Shared by the itinerary
   download popup and the landing signup. Identity is the Shopify session
   (same contract as notify-me): a 401 saves the intent, opens the in-page
   KwikPass login, and the intent completes on the post-login page load.
   There is no status GET for leads — repeat submits are idempotent and answer
   alreadyCaptured, which is how a shopper re-sees their state. */
(function () {
  var LEADS_API = '/apps/lotto-commerce-backend/leads';
  var LEAD_PENDING_KEY = 'stroma:lead:pending';

  /* Saved intents (shared by leads + the shop's notify-me). A 401 saves the
     intent and opens login; it is finished after login and kept in storage
     until the server confirms it (200) — the Shopify session cookie can lag a
     KwikPass login by seconds, so attempts back off over ~15s, and one more
     runs when the tab regains focus. Intents expire after 15 minutes. If they
     still can't be finished, onFail tells the shopper to tap again. */
  var RESUME_DELAYS = [0, 1000, 2000, 4000, 8000];
  var PENDING_TTL = 15 * 60 * 1000;
  var RESUME_FAIL_MSG = "Couldn't complete your sign-up — tap Notify Me again.";

  // KwikPass's OTP iframe is on screen (login still in progress).
  function kpOpen() {
    var f = document.getElementById('iframe-kp');
    if (!f) return false;
    var cs = getComputedStyle(f);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && f.getBoundingClientRect().height > 0;
  }

  // opts: valid(p) well-formed; mine(p) this owner should finish it (else left
  // for another owner); attempt(p) -> Promise<{kind: 'ok'|'auth'|'error'|'retry',
  // message}>; onStart/onEnd(p); onDone(p); onFail(p, message); onLoginReturn().
  // Owners sharing one storage key (the AXIS + ZERO shop modules carry the
  // same products) take turns: only one attempt run per key at a time.
  var activeRun = {};
  function pendingIntent(key, opts) {
    var running = false, loginOpened = false, returnTries = 0;
    function clear(at) {
      try {
        if (at != null) {
          var cur = JSON.parse(localStorage.getItem(key) || 'null');
          if (cur && cur.at !== at) return;
        }
        localStorage.removeItem(key);
      } catch (err) { /* storage blocked */ }
    }
    function read() {
      var raw = null, p = null;
      try { raw = localStorage.getItem(key); } catch (err) { return null; }
      if (!raw) return null;
      try { p = JSON.parse(raw); } catch (err) { /* corrupt: dropped below */ }
      if (!p || !opts.valid(p) || !(Date.now() - Number(p.at) <= PENDING_TTL)) { clear(); return null; }
      return (!opts.mine || opts.mine(p)) ? p : null;
    }
    function save(p) {
      p.at = Date.now();
      try { localStorage.setItem(key, JSON.stringify(p)); } catch (err) { /* storage blocked: the shopper retaps after login */ }
    }
    function run(delays, quiet) {
      if (running || activeRun[key]) return;
      var pend = read();
      if (!pend) return;
      running = true;
      activeRun[key] = true;
      var i = 0;
      if (opts.onStart) opts.onStart(pend);
      function end() { running = false; activeRun[key] = false; if (opts.onEnd) opts.onEnd(pend); }
      (function next() {
        setTimeout(function () {
          var cur = read();
          if (!cur) { end(); return; }
          pend = cur;
          opts.attempt(pend).then(function (res) {
            if (res.kind === 'ok') { clear(pend.at); end(); opts.onDone(pend); return; }
            if (res.kind === 'error') { clear(pend.at); end(); opts.onFail(pend, res.message); return; }
            if (++i < delays.length) { next(); return; }
            // Still unauthorised: say so. The intent stays until it expires, so a
            // later focus or a retap can still finish it.
            // Only the post-reload resume may tell the shopper; focus / popup-close
            // retries stay quiet, and never while the login window is still open.
            end();
            if (!quiet && !kpOpen()) opts.onFail(pend, RESUME_FAIL_MSG);
          });
        }, delays[i]);
      })();
    }
    // Back on the tab: after an in-page login (no reload) run the full backoff
    // once; otherwise give a still-pending intent one more try (bounded).
    function onReturn() {
      if (document.visibilityState === 'hidden' || kpOpen()) return;
      if (loginOpened) {
        loginOpened = false;
        if (opts.onLoginReturn) opts.onLoginReturn();
        run(RESUME_DELAYS, true);
        return;
      }
      // Deferred a tick so an owner that just opened login claims the run first.
      setTimeout(function () {
        if (!running && !activeRun[key] && returnTries < 3 && read()) {
          returnTries++;
          run([0], true);
        }
      }, 0);
    }
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return {
      save: save,
      read: read,
      clear: clear,
      resume: function () { run(RESUME_DELAYS); },
      loginOpened: function () { loginOpened = true; },
      isRunning: function () { return running; }
    };
  }

  window.__stroma = window.__stroma || {};
  window.__stroma.pendingIntent = pendingIntent;

  function openLeadLogin() {
    leadIntent.loginOpened();
    if (typeof window.handleKpAndShopifyLogin === 'function') {
      window.handleKpAndShopifyLogin(window.location.pathname + window.location.search);
      return;
    }
    window.location = '/account/login?return_url=' +
      encodeURIComponent(window.location.pathname + window.location.search);
  }

  function postLead(tag) {
    return fetch(LEADS_API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tag: tag })
    }).then(function (r) {
      return r.json().then(function (j) { return { status: r.status, j: j }; });
    });
  }

  // One capture -> { kind: 'ok'|'auth'|'error'|'retry', already, message }.
  function leadAttempt(tag) {
    return postLead(tag).then(function (res) {
      if (res.status === 200 && res.j && res.j.data) {
        if (!res.j.data.alreadyCaptured) {
          // GTM conversion: a brand-new notify signup the server just confirmed.
          // Fires only here — after OTP login (when needed) and registration both
          // succeed — never on the initial click. Gated on !alreadyCaptured so a
          // re-confirmation (e.g. tapping both the hero and landing buttons) can't
          // double-count.
          (window.dataLayer = window.dataLayer || []).push({ event: 'notify_me_success' });
        }
        return { kind: 'ok', already: !!res.j.data.alreadyCaptured };
      }
      if (res.status === 401) return { kind: 'auth' };
      var apiErr = res.j && res.j.errors && res.j.errors.error && res.j.errors.error[0];
      return {
        kind: res.status < 500 && apiErr && apiErr.message ? 'error' : 'retry',
        message: (apiErr && apiErr.message) || 'Something went wrong. Please try again.'
      };
    }).catch(function () {
      return { kind: 'retry', message: 'Network error. Please try again.' };
    });
  }

  // capture(tag, say[, opts]) -> Promise<boolean>. `say(text)` renders the
  // surface's status line. opts.silent401 marks a background attempt: it must
  // never message or reopen login for a shopper who didn't just act.
  function leadCapture(tag, say, opts) {
    var silent401 = opts && opts.silent401;
    say = say || function () {};
    return leadAttempt(tag).then(function (res) {
      if (res.kind === 'ok') {
        var pend = leadIntent.read();
        if (pend && pend.tag === tag) leadIntent.clear(pend.at);
        say(res.already
          ? "You're already on the list."
          : "You're on the list — we'll be in touch.");
        return true;
      }
      if (res.kind === 'auth') {
        if (silent401) return false;
        leadIntent.save({ tag: tag });
        // The login UI is itself the prompt; echoing a message only leaves text
        // lingering after the shopper dismisses it. Clear the note and open login.
        say('');
        openLeadLogin();
        return false;
      }
      say(res.message);
      return false;
    });
  }

  window.__stroma.leadCapture = leadCapture;

  // Post-login: finish the saved intent. Success signals the surface for this
  // tag (landing / hero / itinerary) to show its on-the-list UI; a failure
  // signals it to show the message near its button.
  var leadIntent = pendingIntent(LEAD_PENDING_KEY, {
    valid: function (p) { return typeof p.tag === 'string' && !!p.tag; },
    attempt: function (p) { return leadAttempt(p.tag); },
    onDone: function (p) {
      document.dispatchEvent(new CustomEvent('stroma:lead-resumed', { detail: { tag: p.tag } }));
    },
    onFail: function (p, message) {
      document.dispatchEvent(new CustomEvent('stroma:lead-resume-failed', { detail: { tag: p.tag, message: message } }));
    }
  });
  function resumeLeadCapture() { leadIntent.resume(); }
  if (window.__stroma.register) window.__stroma.register(resumeLeadCapture);
  else document.addEventListener('DOMContentLoaded', resumeLeadCapture);
})();
})();

/* ==== 30-shop.js ==== */
(function(){ 'use strict';
/* STROMA shop module (sections/stroma-shop.liquid — AXIS + ZERO instances).
   Each instance SSRs its default state (default gender, first colourway) and
   embeds a JSON payload of every colourway (per gender: colour, price, sizes,
   media srcsets). This module drives gender tabs, colourway switching, the
   scroll-snap gallery + counter/arrows, the rail/grid shot tiles, and the
   notify popup. No network requests — everything reads the payload. */

/* Page-level notify-me status: ONE bulk GET (/notify-me/bulk, <=50 ids per
   call) for every shop module's products, shared by all module instances via
   subscribers. Runs on a logged-in page (Liquid hint 1) and after an in-page
   login. Products the answer lacks (or a failed call) keep the module's lazy
   single GET; a 401 means logged out: empty status, no fallback GETs. */
var BULK_API = '/apps/lotto-commerce-backend/notify-me/bulk';
var BULK_MAX = 50;
var pageStatus = { pids: [], subs: [], inflight: null };
function bulkChunk(ids) {
  return fetch(BULK_API + '?productIds=' + ids.join(','))
    .then(function (r) { return r.status === 200 ? r.json() : (r.status === 401 ? { loggedOut: true } : null); })
    .then(function (j) {
      if (!j || j.loggedOut) return j;
      var prods = j.data && j.data.products;
      return Array.isArray(prods) ? { products: prods } : null;
    })
    .catch(function () { return null; });
}
// -> Promise<{ loggedOut, maps: { pid: { vid | '': true } } }>; maps holds
// only the products the server answered for.
pageStatus.run = function () {
  if (pageStatus.inflight) return pageStatus.inflight;
  var ids = pageStatus.pids.slice(), chunks = [];
  for (var i = 0; i < ids.length; i += BULK_MAX) chunks.push(ids.slice(i, i + BULK_MAX));
  pageStatus.inflight = Promise.all(chunks.map(bulkChunk)).then(function (parts) {
    var res = { loggedOut: false, maps: {} };
    parts.forEach(function (part) {
      if (!part) return;
      if (part.loggedOut) { res.loggedOut = true; return; }
      part.products.forEach(function (e) {
        if (!e || !/^\d+$/.test(String(e.productId)) || !Array.isArray(e.registrations)) return;
        var map = {};
        e.registrations.forEach(function (reg) { map[(reg && reg.variantId) ? String(reg.variantId) : ''] = true; });
        res.maps[String(e.productId)] = map;
      });
    });
    if (res.loggedOut) res.maps = {};
    pageStatus.inflight = null;
    pageStatus.subs.forEach(function (fn) { try { fn(res); } catch (e) { console.error('[stroma]', e); } });
    return res;
  });
  return pageStatus.inflight;
};
window.__stroma.notifyStatus = pageStatus;

function initShop() {
  var STAGE_SIZES = '(max-width: 767px) 100vw, (max-width: 1440px) 57vw, 820px';
  var TILE_SIZES = '(max-width: 767px) 50vw, (max-width: 1440px) 21.4vw, 307px';
  var REDUCED = !!(window.__stroma && window.__stroma.reducedMotion);

  var roots = document.querySelectorAll('[data-st-shop]');
  Array.prototype.forEach.call(roots, function (root) {
    try { setup(root); } catch (e) { console.error('[stroma] shop init', e); }
  });
  // Every module has registered its products: one status call for the page.
  if (pageStatus.pids.length && roots[0].getAttribute('data-st-logged-in') === '1') pageStatus.run();

  function setup(root) {
    var dataEl = root.querySelector('script[data-st-shop-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[stroma] shop payload parse failed', e); return; }

    var strip = root.querySelector('[data-st-strip]');
    var counter = root.querySelector('[data-st-counter]');
    var thumbsWrap = root.querySelector('[data-st-thumbs]');
    var nameEl = root.querySelector('[data-st-name]');
    var priceEl = root.querySelector('[data-st-price]');
    var shotWraps = root.querySelectorAll('[data-st-shots]');
    var tabs = root.querySelectorAll('[data-st-gender]');
    var addBtn = root.querySelector('[data-st-add]');
    var ddEl = root.querySelector('[data-st-dd]');
    var ddList = ddEl && ddEl.querySelector('[data-st-dd-list]');
    var ddLoader = root.querySelector('.st-shop__dd-loader');
    var BELL = '<svg class="lnm-bell" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6v-5a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"/></svg>';
    var PLUS = '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true"><path d="M5.5 0v11M0 5.5h11" stroke="currentColor" stroke-width="1.2"/></svg>';
    var prevBtn = root.querySelector('[data-st-prev]');
    var nextBtn = root.querySelector('[data-st-next]');

    var state = {
      gender: root.getAttribute('data-default-gender') || 'men',
      idx: 0,
      mediaIdx: 0
    };

    if (REDUCED) {
      Array.prototype.forEach.call(root.querySelectorAll('video[autoplay]'), function (v) {
        v.removeAttribute('autoplay');
        try { v.pause(); } catch (e) { /* not started yet */ }
      });
    }

    function list() { return (data.genders && data.genders[state.gender]) || []; }
    function active() { return list()[state.idx] || null; }

    function findProduct(handle) {
      var keys = ['men', 'women'];
      for (var k = 0; k < keys.length; k++) {
        var arr = data.genders[keys[k]] || [];
        for (var i = 0; i < arr.length; i++) {
          if (arr[i].handle === handle) return arr[i];
        }
      }
      return null;
    }

    /* ---- gallery strip ---- */
    function mediaCount() {
      var p = active();
      return p ? p.media.length : 0;
    }

    function updateCounter(i) {
      if (!counter) return;
      var n = mediaCount();
      counter.textContent = n > 0 ? (i + 1) + ' / ' + n : '';
    }

    function updateArrows(i) {
      var n = mediaCount();
      if (prevBtn) prevBtn.disabled = i <= 0;
      if (nextBtn) nextBtn.disabled = i >= n - 1;
    }

    function buildStage() {
      if (!strip) return;
      var p = active();
      strip.innerHTML = '';
      if (!p) return;
      p.media.forEach(function (m) {
        var slide = document.createElement('div');
        slide.className = 'st-shop__slide';
        var img = document.createElement('img');
        img.src = m.u;
        img.srcset = m.ss;
        img.sizes = STAGE_SIZES;
        img.alt = m.a || '';
        img.width = m.w;
        img.height = m.h;
        img.loading = 'lazy';
        img.draggable = false;
        slide.appendChild(img);
        strip.appendChild(slide);
        window.__stroma.smartCrop(img);
      });
      strip.scrollLeft = 0;
      state.mediaIdx = 0;
      updateCounter(0);
      updateArrows(0);
    }

    if (strip) {
      /* First render is SSR'd in Liquid; buildStage only runs on colourway/
         gender switches — SSR slides need crop placement too. */
      Array.prototype.forEach.call(strip.querySelectorAll('.st-shop__slide img'), window.__stroma.smartCrop);
      var ticking = false;
      strip.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var w = strip.clientWidth;
          if (w <= 0) return;
          var i = Math.round(strip.scrollLeft / w);
          var n = mediaCount();
          if (i < 0) i = 0;
          if (n > 0 && i > n - 1) i = n - 1;
          if (i !== state.mediaIdx) {
            state.mediaIdx = i;
            updateCounter(i);
          }
          updateArrows(i);
        });
      }, { passive: true });
    }

    /* Tap the hero art (not a swipe) to open the active colourway's product
       page. A horizontal swipe scrolls the native gallery and fires no click on
       touch; on desktop we suppress navigation once the pointer has dragged past
       a small threshold so dragging the strip never navigates. */
    if (strip) {
      var heroDownX = 0, heroDownY = 0, heroMoved = false;
      strip.addEventListener('pointerdown', function (e) {
        heroDownX = e.clientX; heroDownY = e.clientY; heroMoved = false;
      }, { passive: true });
      strip.addEventListener('pointermove', function (e) {
        if (Math.abs(e.clientX - heroDownX) > 8 || Math.abs(e.clientY - heroDownY) > 8) heroMoved = true;
      }, { passive: true });
      strip.addEventListener('click', function () {
        if (heroMoved) return;
        var p = active();
        if (p && p.handle) window.location.href = '/products/' + p.handle;
      });
    }

    function scrollToSlide(i) {
      if (!strip) return;
      strip.scrollTo({
        left: i * strip.clientWidth,
        behavior: REDUCED ? 'auto' : 'smooth'
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { scrollToSlide(state.mediaIdx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollToSlide(state.mediaIdx + 1); });

    /* ---- colourway thumbs ---- */
    function buildThumbs() {
      if (!thumbsWrap) return;
      thumbsWrap.innerHTML = '';
      list().forEach(function (p, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'st-shop__thumb' + (i === state.idx ? ' is-active' : '');
        btn.setAttribute('data-st-cw', String(i));
        btn.setAttribute('aria-pressed', i === state.idx ? 'true' : 'false');
        btn.setAttribute('aria-label', p.colour);
        if (p.cut || p.media[0]) {
          var img = document.createElement('img');
          img.src = p.cut || p.media[0].t;
          if (p.cut) img.className = 'st-shop__thumbcut';
          img.alt = '';
          img.width = 62;
          img.height = 31;
          img.loading = 'lazy';
          btn.appendChild(img);
        }
        thumbsWrap.appendChild(btn);
      });
    }

    if (thumbsWrap) {
      thumbsWrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-st-cw]') : null;
        if (!btn) return;
        var i = parseInt(btn.getAttribute('data-st-cw'), 10);
        if (!isFinite(i) || i === state.idx) return;
        state.idx = i;
        buildStage();
        buildThumbs();
        buildMeta();
        buildShots();
      });
    }

    /* ---- meta ---- */
    function buildMeta() {
      var p = active();
      if (nameEl) nameEl.textContent = p ? p.colour : '';
      if (priceEl) priceEl.textContent = p ? p.price : '';
    }

    /* ---- shot tiles (desktop rail + mobile grid share the logic) ---- */
    function shots() {
      var l = list();
      var act = active();
      var out = [];
      for (var i = 0; i < l.length; i++) {
        if (i !== state.idx && l[i].media[0]) {
          out.push({ m: l[i].media[0], handle: l[i].handle, colour: l[i].colour });
        }
      }
      if (act) {
        for (var j = 1; j < act.media.length && out.length < 3; j++) {
          out.push({ m: act.media[j], handle: act.handle, colour: act.colour });
        }
      }
      return out.slice(0, 3);
    }

    function buildShots() {
      var s = shots();
      Array.prototype.forEach.call(shotWraps, function (wrap) {
        var tiles = wrap.querySelectorAll('[data-st-tile]');
        var copy = wrap.querySelector('.st-shop__copy');
        // top up missing tile skeletons by cloning the last one
        while (tiles.length > 0 && tiles.length < s.length && tiles.length < 3) {
          var clone = tiles[tiles.length - 1].cloneNode(true);
          clone.className = 'st-shop__tile st-shop__tileslot-' + (tiles.length + 1);
          if (copy) wrap.insertBefore(clone, copy); else wrap.appendChild(clone);
          tiles = wrap.querySelectorAll('[data-st-tile]');
        }
        Array.prototype.forEach.call(tiles, function (tile, i) {
          var shot = s[i];
          var img = tile.querySelector('[data-st-tile-img]');
          var btn = tile.querySelector('[data-st-tile-add]');
          if (!shot) { tile.style.display = 'none'; return; }
          tile.style.display = '';
          if (img) {
            img.hidden = false;
            img.src = shot.m.u;
            img.srcset = shot.m.ss;
            img.sizes = TILE_SIZES;
            img.width = shot.m.w;
            img.height = shot.m.h;
          }
          if (btn) {
            btn.setAttribute('data-st-handle', shot.handle);
            btn.setAttribute('aria-label', 'Notify me — ' + shot.colour);
          }
        });
      });
      paintCtas();
    }

    Array.prototype.forEach.call(shotWraps, function (wrap) {
      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-st-tile-add]') : null;
        if (btn) {
          var p = findProduct(btn.getAttribute('data-st-handle'));
          if (p) { if (isBuy(p)) openBuy(p, btn); else openPopup(p, btn); }
          return;
        }
        // Clicking a tile's art (anywhere but the + notify button) opens that
        // colourway's product page. The handle rides the tile's add button and
        // is kept current by buildShots on every colourway/gender switch.
        var tile = e.target.closest ? e.target.closest('[data-st-tile]') : null;
        if (!tile) return;
        var addBtn = tile.querySelector('[data-st-tile-add]');
        var handle = addBtn && addBtn.getAttribute('data-st-handle');
        if (handle) window.location.href = '/products/' + handle;
      });
    });

    /* ---- gender tabs ---- */
    /* A gender-locked module renders no [data-st-gender] tabs: `tabs` is empty,
       so no wiring happens and state.gender stays the SSR'd data-default-gender
       (the locked gender). */
    Array.prototype.forEach.call(tabs, function (tab) {
      tab.addEventListener('click', function () {
        var g = tab.getAttribute('data-st-gender');
        if (g === state.gender) return;
        if (!data.genders[g] || data.genders[g].length === 0) return;
        state.gender = g;
        state.idx = 0;
        Array.prototype.forEach.call(tabs, function (t) {
          var on = t.getAttribute('data-st-gender') === g;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        buildStage();
        buildThumbs();
        buildMeta();
        buildShots();
      });
    });

    /* ---- notify popup ---- */
    var overlay = root.querySelector('[data-st-popup]');
    var sizesWrap = overlay ? overlay.querySelector('[data-st-sizes]') : null;
    var popupPrice = overlay ? overlay.querySelector('[data-st-popup-price]') : null;
    var form = overlay ? overlay.querySelector('[data-st-notify-form]') : null;
    var emailInput = overlay ? overlay.querySelector('[data-st-email]') : null;
    var stubMsg = overlay ? overlay.querySelector('[data-st-stubmsg]') : null;
    var closeBtn = overlay ? overlay.querySelector('.st-shop__close') : null;
    var popupProduct = null;
    var popupSize = null;
    var popupVid = null;
    var registeredVids = null; // null until the status GET answers; '' = product-level row
    var notifyBusy = false;
    var lastFocus = null;
    var notifyLabel = overlay ? overlay.querySelector('[data-st-notify-label]') : null;
    var notifyBtn = form ? form.querySelector('.st-shop__notify') : null;

    function eurFor(size) {
      var m = /(\d+(?:\.\d+)?)/.exec(size);
      if (!m) return '';
      var uk = parseFloat(m[1]);
      if (!isFinite(uk)) return '';
      // Design mapping: UK 5 -> (EUR 39), i.e. EUR = UK + 34.
      return '(EUR ' + Math.round(uk + 34) + ')';
    }

    function buildSizeRows(p) {
      if (!sizesWrap) return;
      sizesWrap.innerHTML = '';
      popupSize = null;
      if (!p.sizes || p.sizes.length === 0) {
        var none = document.createElement('p');
        none.className = 'st-shop__nosizes';
        none.textContent = 'Sizes to be announced';
        sizesWrap.appendChild(none);
        return;
      }
      p.sizes.forEach(function (size) {
        var sizeLabel = (size && size.label) ? size.label : size;
        var sizeVid = (size && size.vid) ? String(size.vid) : null;
        var row = document.createElement('button');
        row.type = 'button';
        row.className = 'st-shop__sizerow';
        if (sizeVid) row.setAttribute('data-vid', sizeVid);
        row.setAttribute('aria-pressed', 'false');

        var mark = document.createElement('span');
        mark.className = 'st-shop__sizemark';
        mark.setAttribute('aria-hidden', 'true');
        row.appendChild(mark);

        var label = document.createElement('span');
        label.className = 'st-shop__sizelabel';
        label.textContent = sizeLabel;
        row.appendChild(label);

        var eur = eurFor(sizeLabel);
        if (eur) {
          var eurEl = document.createElement('span');
          eurEl.className = 'st-shop__sizeeur';
          eurEl.textContent = eur;
          row.appendChild(eurEl);
        }

        var soon = document.createElement('span');
        soon.className = 'st-shop__sizesoon';
        soon.textContent = 'Coming soon';
        row.appendChild(soon);

        row.addEventListener('click', function () {
          Array.prototype.forEach.call(sizesWrap.children, function (r) {
            r.classList.remove('is-selected');
            r.setAttribute('aria-pressed', 'false');
          });
          row.classList.add('is-selected');
          row.setAttribute('aria-pressed', 'true');
          popupSize = sizeLabel;
          popupVid = sizeVid;
          updateNotifyUi();
        });

        sizesWrap.appendChild(row);
      });
      // No size is pre-selected — the shopper chooses their size before registering.
    }

    function openPopup(p, opener) {
      // Re-entrancy guard: nothing on the page traps focus inside the open
      // dialog, so a trigger behind the overlay is still keyboard-reachable;
      // re-running the open path would re-lock against the pinned-state
      // scrollY of 0 and corrupt the close restore point.
      if (!overlay || overlay.classList.contains('is-open')) return;
      popupProduct = p;
      registeredVids = statusMaps[String(p.id)] || loadRegsCache(String(p.id));
      buildSizeRows(p);
      if (popupPrice) popupPrice.textContent = p.price;
      if (stubMsg) { stubMsg.hidden = true; stubMsg.textContent = ''; }
      updateNotifyUi();
      fetchNotifyStatus(p);
      lastFocus = opener || document.activeElement;
      overlay.classList.add('is-open');
      window.__stroma.scrollLock.lock();
      if (closeBtn) closeBtn.focus();
    }

    function closePopup() {
      if (!overlay || !overlay.classList.contains('is-open')) return;
      overlay.classList.remove('is-open');
      window.__stroma.scrollLock.unlock();
      // preventScroll: unlock() has just restored the exact pre-open scroll
      // position; letting focus scroll the trigger into view would move the
      // page somewhere the user never was.
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      lastFocus = null;
    }

    if (overlay) {
      overlay.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-st-popup-close]') : null;
        if (btn) closePopup();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePopup();
      });
    }

    /* ---- buy mode: launched colourways (custom.notify_me not true) ----
       Same as the Ginza page: desktop opens the anchored Select Size drawer
       (.option-dropdown rows -> theme-global plpAtc: cart add, count bubble,
       product toast); mobile opens the theme quick-add sheet (openQuickView).
       Coming-soon colourways keep the Notify Me popup above. */
    // Strictly false (not missing): HTML cached before this payload field existed stays Notify Me.
    var PREVIEW_BUY = root.getAttribute('data-st-preview-buy') === 'true';
    function isBuy(p) { return !!p && (p.notify === false || (PREVIEW_BUY && p.notify === true)); }
    // Switch a button between the buy look (SSR "+ Add" / "+") and the Notify
    // Me look lotto-notify-me.js gives coming-soon buttons (bell + label).
    function setBuyLook(btn, buy, kind) {
      if (!btn) return;
      var was = btn.hasAttribute('data-st-buy');
      if (buy) {
        if (was && btn.__stLook === 'buy') return;
        btn.setAttribute('data-st-buy', '');
        btn.setAttribute('data-st-notify', 'false');
        btn.classList.remove('lnm-st-cta', 'lnm-st-plus', 'is-registered');
        btn.style.width = ''; btn.style.padding = '';
        // Photo tiles get a small "+ Add" button (styled like their Notify Me button)
        // rather than a bare "+", so every buyable colour reads as add-to-bag.
        if (kind !== 'add') btn.classList.add('st-shop__plus--add');
        btn.innerHTML = '<span class="st-label">+ Add</span>';
        btn.__stIdle = null; btn.__stLook = 'buy';
        return;
      }
      btn.setAttribute('data-st-notify', 'true');
      if (!was && btn.__stLook !== 'buy' && btn.querySelector('.lnm-bell')) return; // already the Notify Me look
      btn.removeAttribute('data-st-buy');
      btn.classList.remove('st-shop__plus--add');
      btn.classList.add(kind === 'add' ? 'lnm-st-cta' : 'lnm-st-plus');
      btn.innerHTML = BELL + '<span class="st-label">Notify Me</span>';
      btn.__stIdle = 'Notify Me'; btn.__stLook = 'notify';
    }
    function closeDd() {
      if (!ddEl) return;
      ddEl.classList.remove('active');
      if (ddEl.__stFor) ddEl.__stFor.classList.remove('active');
      ddEl.__stFor = null; ddEl.__stPid = null;
    }
    function placeDd() {
      var btn = ddEl && ddEl.__stFor;
      if (!btn) return;
      var b = btn.getBoundingClientRect();
      var w = ddEl.offsetWidth, h = ddEl.offsetHeight, gap = 12;
      var left = b.right + gap;
      if (left + w > window.innerWidth - 8) left = Math.max(8, b.left - w - gap);
      var top = b.top + b.height / 2 - h / 2;
      top = Math.max(8, Math.min(top, window.innerHeight - h - 8));
      ddEl.style.left = Math.round(left) + 'px';
      ddEl.style.top = Math.round(top) + 'px';
    }
    function openBuy(p, btn) {
      var mobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      if (mobile || !ddEl || !ddList) {
        if (typeof window.openQuickView === 'function') {
          btn.setAttribute('data-handle', p.handle);
          window.openQuickView(btn, p.handle);
        } else {
          window.location.href = '/products/' + p.handle;
        }
        return;
      }
      if (ddEl.classList.contains('active') && ddEl.__stFor === btn) { closeDd(); return; }
      closeDd();
      var loader = ddLoader ? ddLoader.innerHTML : '';
      ddList.innerHTML = '';
      (p.sizes || []).forEach(function (sz) {
        var row = document.createElement('button');
        row.type = 'button';
        row.className = 'plp-size-value';
        row.setAttribute('data-handle', p.handle);
        if (sz.vid) row.setAttribute('data-vid', sz.vid);
        if (!sz.vid || sz.avail === false) row.disabled = true;
        row.innerHTML = '<span class="default"></span><span class="loader">' + loader + '</span>';
        row.firstChild.textContent = sz.label;
        row.addEventListener('click', function (e) {
          e.stopPropagation();
          if (typeof window.plpAtc === 'function') window.plpAtc(row);
        });
        ddList.appendChild(row);
      });
      ddEl.__stFor = btn;
      ddEl.__stPid = String(p.id);
      ddEl.classList.add('active');
      btn.classList.add('active');
      placeDd();
    }
    if (ddEl) {
      // Ginza closes its drawer when the pointer leaves it; keep that, plus
      // outside click, Escape, and a re-anchor while the page scrolls/resizes.
      ddEl.addEventListener('mouseleave', closeDd);
      document.addEventListener('click', function (e) {
        if (!ddEl.classList.contains('active')) return;
        if (ddEl.contains(e.target)) return;
        if (ddEl.__stFor && ddEl.__stFor.contains(e.target)) return;
        closeDd();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDd(); });
      window.addEventListener('scroll', function () { if (ddEl.classList.contains('active')) placeDd(); }, { passive: true });
      window.addEventListener('resize', closeDd);
    }

    if (addBtn) {
      addBtn.addEventListener('pointerdown', function () {
        var p = active();
        if (p && !isBuy(p)) fetchNotifyStatus(p);
      });
      addBtn.addEventListener('click', function () {
        var p = active();
        if (!p) return;
        if (isBuy(p)) openBuy(p, addBtn); else openPopup(p, addBtn);
      });
    }

    /* ---- notify-me API (Lotto commerce backend, signed App Proxy) ----
       Identity is the store login: 401 means logged out — we save the intent,
       open the KwikPass login, and finish the registration on its success
       event. Repeat registrations are idempotent server-side. */
    var NOTIFY_API = '/apps/lotto-commerce-backend/notify-me';
    var NOTIFY_PENDING_KEY = 'stroma:notify:pending';
    // Liquid's 1/0 login hint: a logged-out page skips the status GET (it can
    // only answer 401). Any 200 from the API on this page overrides it.
    var loggedOutHint = root.getAttribute('data-st-logged-in') === '0';
    var loggedIn = false;
    var notifyResuming = false;
    var confirmedRegs = {}; // pid -> { vid: true } confirmed by a POST on this page
    var statusMaps = {}; // pid -> status map answered on this page (bulk / GET / POST)
    var bulkPids = {}; // pid -> true: the page bulk answered it (no single GET needed)
    var REG_LABEL = "You'll be notified";

    function eachPid(fn) {
      ['men', 'women'].forEach(function (g) {
        ((data.genders && data.genders[g]) || []).forEach(function (x) {
          if (/^\d+$/.test(String(x.id))) fn(String(x.id));
        });
      });
    }

    // Module CTAs: the main button follows the active colourway, each tile pill
    // its own colourway — "You'll be notified" once any size is registered.
    // They still open the popup, so more sizes can be added.
    function hasReg(pid) {
      var m = statusMaps[String(pid)];
      if (!m) return false;
      for (var k in m) { if (m[k] === true) return true; }
      return false;
    }
    function paintCta(btn, on) {
      var label = btn.querySelector('.st-label');
      if (label) {
        if (btn.__stIdle == null) btn.__stIdle = label.textContent; // SSR label
        label.textContent = on ? REG_LABEL : btn.__stIdle;
      }
      btn.classList.toggle('is-registered', on);
    }
    function paintCtas() {
      var p = active();
      root.setAttribute('data-st-notify-mode', isBuy(p) ? 'false' : 'true');
      if (ddEl && ddEl.__stFor === addBtn && p && ddEl.__stPid !== String(p.id)) closeDd();
      if (addBtn && isBuy(p)) {
        setBuyLook(addBtn, true, 'add');
        addBtn.setAttribute('aria-label', 'Add to cart — ' + p.colour);
      } else if (addBtn) {
        setBuyLook(addBtn, false, 'add');
        var addOn = !!p && hasReg(p.id);
        paintCta(addBtn, addOn);
        // The registered label is wider than a fixed-width "+ Add": size to it
        // (same auto width + 10px padding as the notify variant).
        addBtn.style.width = addOn ? 'auto' : '';
        addBtn.style.padding = addOn ? '0 10px' : '';
      }
      Array.prototype.forEach.call(shotWraps, function (wrap) {
        Array.prototype.forEach.call(wrap.querySelectorAll('[data-st-tile-add]'), function (btn) {
          var tp = findProduct(btn.getAttribute('data-st-handle'));
          if (isBuy(tp)) {
            setBuyLook(btn, true, 'plus');
            btn.setAttribute('aria-label', 'Add to cart — ' + tp.colour);
            return;
          }
          setBuyLook(btn, false, 'plus');
          var on = !!tp && hasReg(tp.id);
          paintCta(btn, on);
          if (tp) btn.setAttribute('aria-label', (on ? REG_LABEL : 'Notify me') + ' — ' + tp.colour);
        });
      });
    }

    function notifyMsg(text) {
      if (stubMsg) { stubMsg.textContent = text; stubMsg.hidden = !text; }
    }

    function isRegistered(vid) {
      if (!registeredVids) return false;
      // A product-level registration ('' key) covers every size.
      return registeredVids[''] === true || (vid && registeredVids[vid] === true);
    }

    function updateNotifyUi() {
      if (!notifyBtn || !notifyLabel) return;
      if (sizesWrap) {
        Array.prototype.forEach.call(sizesWrap.children, function (r) {
          var soon = r.querySelector && r.querySelector('.st-shop__sizesoon');
          if (!soon) return;
          var vid = r.getAttribute ? r.getAttribute('data-vid') : null;
          soon.textContent = isRegistered(vid) ? "You're in" : 'Coming soon';
        });
      }
      if (isRegistered(popupVid)) {
        notifyLabel.textContent = REG_LABEL;
        notifyBtn.disabled = true;
      } else {
        notifyLabel.textContent = (notifyBusy || notifyResuming) ? 'One moment…' : 'Notify Me';
        notifyBtn.disabled = notifyBusy || notifyResuming;
      }
    }

    // Registration-status cache (sessionStorage, per product). Lets the popup
    // paint "You're in" instantly on open instead of waiting for the network.
    // Safe to trust as a first paint: registrations only ever grow, and the
    // GET that follows every open replaces the cache wholesale — so a stale
    // entry (e.g. a different shopper on this browser) is corrected within a
    // beat, and a 401 clears it. Session-scoped so it dies with the tab.
    function regsCacheKey(pid) { return 'stroma:notify:regs:' + pid; }
    function loadRegsCache(pid) {
      try {
        var raw = sessionStorage.getItem(regsCacheKey(pid));
        return raw ? JSON.parse(raw) : null;
      } catch (err) { return null; }
    }
    function saveRegsCache(pid, map) {
      try { sessionStorage.setItem(regsCacheKey(pid), JSON.stringify(map || {})); } catch (err) {}
    }

    function rememberRegistration(variantId, pid) {
      registeredVids = registeredVids || {};
      registeredVids[variantId || ''] = true;
      var key = pid || (popupProduct && String(popupProduct.id));
      if (key) {
        (confirmedRegs[key] = confirmedRegs[key] || {})[variantId || ''] = true;
        (statusMaps[key] = statusMaps[key] || {})[variantId || ''] = true;
        saveRegsCache(key, registeredVids);
      }
      updateNotifyUi();
      paintCtas();
    }

    // Registrations only grow: merge a status answer with the ones a POST on
    // this page just confirmed (a GET can race a resume's POST).
    function applyStatus(pid, map) {
      var mine = confirmedRegs[pid];
      if (mine) Object.keys(mine).forEach(function (k) { map[k] = true; });
      saveRegsCache(pid, map);
      statusMaps[pid] = map;
      paintCtas();
      // The server already has the saved intent: nothing left to resume.
      var pend = notifyIntent && notifyIntent.read();
      if (pend && String(pend.body.productId) === pid &&
          (map[''] === true || map[pend.body.variantId || ''] === true)) notifyIntent.clear(pend.at);
      if (popupProduct && String(popupProduct.id) === pid) {
        registeredVids = map;
        updateNotifyUi();
      }
    }

    // Dedupe: +Add prefetch and popup-open both call this; skip the second
    // request when one for the same product fired moments ago. force: ignore
    // the logged-out hint and the dedupe (after an in-page login).
    var lastStatusFetch = { pid: null, at: 0 };
    function fetchNotifyStatus(p, force) {
      var pid = String(p.id);
      if (!force && !loggedIn && loggedOutHint) {
        // Logged out per Liquid: apply the 401 answer locally, no round trip.
        applyStatus(pid, {});
        return;
      }
      if (!force) {
        // The page bulk is in flight: decide once it answers.
        if (pageStatus.inflight) { pageStatus.inflight.then(function () { fetchNotifyStatus(p); }); return; }
        if (bulkPids[pid]) return; // already answered by the page bulk
      }
      var now = Date.now();
      if (!force && lastStatusFetch.pid === pid && now - lastStatusFetch.at < 1500) return;
      lastStatusFetch = { pid: pid, at: now };
      fetch(NOTIFY_API + '?productId=' + encodeURIComponent(pid))
        .then(function (r) { return r.status === 200 ? r.json() : (r.status === 401 ? { loggedOut: true } : null); })
        .then(function (j) {
          if (!j) return;
          var map = {};
          if (!j.loggedOut && j.data) {
            loggedIn = true;
            (j.data.registrations || []).forEach(function (reg) {
              map[reg.variantId || ''] = true;
            });
          }
          applyStatus(pid, map);
        })
        .catch(function () { /* transient — the POST path reports */ });
    }

    // The notify-me API authorises on the shopper's SHOPIFY customer session
    // (App Proxy `logged_in_customer_id`); KwikPass is configured
    // isShopifySessionEnabled:false, so that session can lag (or miss) a
    // KwikPass login. Open the in-page KwikPass login; the saved intent is
    // finished by notifyIntent on the next load (KwikPass reload) or when the
    // tab regains focus (in-page login) — event-driven, bounded, no polling.
    // Only when KwikPass is absent (theme share preview) fall back to a native
    // redirect.
    function openLogin() {
      notifyIntent.loginOpened();
      if (typeof window.handleKpAndShopifyLogin === 'function') {
        window.handleKpAndShopifyLogin(window.location.pathname + window.location.search);
        return;
      }
      window.location = '/account/login?return_url=' +
        encodeURIComponent(window.location.pathname + window.location.search);
    }

    // -> Promise<{ kind: 'ok'|'auth'|'error' (definitive 4xx)|'retry', message }>
    function postNotify(body, isResume) {
      notifyBusy = true;
      updateNotifyUi();
      return fetch(NOTIFY_API, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
        .then(function (res) {
          notifyBusy = false;
          if (res.status === 200 && res.j.data && res.j.data.registered) {
            var notifyWasRegistered = isRegistered(body.variantId || '');
            rememberRegistration(body.variantId || '', String(body.productId));
            if (!notifyWasRegistered) {
              // GTM conversion: a brand-new back-in-stock notify signup, server-confirmed.
              // Same event as the launch waitlist; fires only after OTP login (when needed)
              // + registration, never on the click, and only for a newly-registered variant.
              (window.dataLayer = window.dataLayer || []).push({ event: 'notify_me_success' });
            }
            // Success is conveyed by the button's "You'll be notified" state; clear any
            // prior message rather than adding a confirmation line below the CTA.
            notifyMsg('');
            loggedIn = true;
            if (!isResume) {
              var pend = notifyIntent.read();
              if (pend && String(pend.body.productId) === String(body.productId) &&
                  (pend.body.variantId || '') === (body.variantId || '')) notifyIntent.clear(pend.at);
            }
            return { kind: 'ok' };
          }
          if (res.status === 401) {
            if (isResume) {
              // Background resume: the retry/backoff owner reports the outcome.
              updateNotifyUi();
              return { kind: 'auth' };
            }
            notifyIntent.save({ body: body });
            // The login UI is itself the prompt — no redundant message line below the CTA.
            notifyMsg('');
            updateNotifyUi();
            openLogin();
            return { kind: 'auth' };
          }
          var apiErr = res.j && res.j.errors && res.j.errors.error && res.j.errors.error[0];
          var message = (apiErr && apiErr.message) || 'Something went wrong. Please try again.';
          if (!isResume) notifyMsg(message);
          updateNotifyUi();
          return { kind: res.status < 500 && apiErr && apiErr.message ? 'error' : 'retry', message: message };
        })
        .catch(function () {
          notifyBusy = false;
          if (!isResume) notifyMsg('Network error. Please try again.');
          updateNotifyUi();
          return { kind: 'retry', message: 'Network error. Please try again.' };
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!popupProduct || notifyBusy || notifyResuming || isRegistered(popupVid)) return;
        var body = { productId: String(popupProduct.id), handle: popupProduct.handle };
        if (popupVid) body.variantId = popupVid;
        postNotify(body);
      });
    }

    // A login that navigated away (classic Shopify login, or KwikPass redirect)
    // reloads the page; an in-page KwikPass login returns focus to the tab.
    // Either way the saved intent is finished by notifyIntent (stroma's shared
    // pending-intent helper): kept until a 200, retried on backoff while the
    // Shopify session cookie catches up, and reported if it never does.

    // Show the outcome of a background registration: switch the module to the
    // registered product's gender + colourway and open the popup with the
    // registered size — showing "You'll be notified", or `msg` (a failure) next
    // to the button. The popup is a modal, so no page scroll is needed.
    function showRegistration(body, msg) {
      if (overlay && overlay.classList.contains('is-open')) {
        if (msg && popupProduct && String(popupProduct.id) === String(body.productId)) notifyMsg(msg);
        return;
      }
      var loc = null;
      ['men', 'women'].forEach(function (g) {
        var arr = (data.genders && data.genders[g]) || [];
        for (var i = 0; i < arr.length; i++) {
          if (String(arr[i].id) === String(body.productId)) loc = { g: g, i: i, p: arr[i] };
        }
      });
      var p = loc ? loc.p : active();
      if (!p || String(p.id) !== String(body.productId)) return;
      if (loc && (state.gender !== loc.g || state.idx !== loc.i)) {
        state.gender = loc.g;
        state.idx = loc.i;
        Array.prototype.forEach.call(tabs, function (t) {
          var on = t.getAttribute('data-st-gender') === loc.g;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        buildStage();
        buildThumbs();
        buildMeta();
        buildShots();
      }
      popupVid = body.variantId || null;
      // Jump the page to this module, then open the popup in the same frame.
      requestAnimationFrame(function () {
        var rect = root.getBoundingClientRect();
        var y = Math.max(0, window.pageYOffset + rect.top - 56);
        // The theme sets CSS scroll-behavior:smooth on html+body, which
        // smooths EVERY programmatic scroll (scrollTo and scrollTop alike),
        // so the jump would still be mid-flight when the popup's scroll-lock
        // reads scrollY and pins the top. Force an instant jump by toggling
        // scroll-behavior off around the scroll, then open the popup.
        var de = document.documentElement, bd = document.body;
        var ph = de.style.scrollBehavior, pb = bd.style.scrollBehavior;
        de.style.scrollBehavior = 'auto';
        bd.style.scrollBehavior = 'auto';
        window.scrollTo(0, y);
        de.style.scrollBehavior = ph;
        bd.style.scrollBehavior = pb;
        openPopup(p, addBtn);
        if (msg) notifyMsg(msg);
      });
    }

    function hasProduct(pid) {
      var found = false;
      ['men', 'women'].forEach(function (g) {
        ((data.genders && data.genders[g]) || []).forEach(function (x) {
          if (String(x.id) === String(pid)) found = true;
        });
      });
      return found;
    }

    // One instance per shop module (AXIS + ZERO share the storage key): each
    // finishes only intents for its own products.
    var notifyIntent = window.__stroma.pendingIntent(NOTIFY_PENDING_KEY, {
      valid: function (p) { return !!p.body && /^\d+$/.test(String(p.body.productId || '')); },
      mine: function (p) { return hasProduct(p.body.productId); },
      attempt: function (p) { return postNotify(p.body, true); },
      onStart: function () { notifyResuming = true; updateNotifyUi(); },
      onEnd: function () { notifyResuming = false; updateNotifyUi(); },
      onDone: function (p) { showRegistration(p.body); },
      onFail: function (p, message) { showRegistration(p.body, message); },
      // In-page login just closed: refresh status once — one bulk call for every
      // product on the page (single GET when the page has just one, or for the
      // current product when the bulk can't answer it).
      onLoginReturn: function () {
        var p = popupProduct || active();
        if (pageStatus.pids.length > 1) {
          pageStatus.run().then(function (res) {
            if (p && !res.loggedOut && !res.maps[String(p.id)]) fetchNotifyStatus(p, true);
          });
        } else if (p) fetchNotifyStatus(p, true);
      }
    });

    // Join the page-level bulk status (initShop fires it once every module is set up).
    eachPid(function (pid) { if (pageStatus.pids.indexOf(pid) < 0) pageStatus.pids.push(pid); });
    pageStatus.subs.push(function (res) {
      if (res.loggedOut) {
        // 401: logged out — empty status, no fallback GETs until a POST says otherwise.
        loggedIn = false;
        loggedOutHint = true;
        eachPid(function (pid) { applyStatus(pid, {}); });
        return;
      }
      eachPid(function (pid) {
        var m = res.maps[pid];
        if (!m) return; // not answered: the lazy single GET still covers it
        loggedIn = true;
        bulkPids[pid] = true;
        var copy = {};
        Object.keys(m).forEach(function (k) { copy[k] = m[k]; });
        applyStatus(pid, copy);
      });
    });
    notifyIntent.resume();

    /* SSR already matches the initial state (default gender, first colourway),
       so no initial rebuild — just seed the arrow disabled states. */
    updateArrows(0);
    // Seed each button's buy / Notify Me look and the module's price mode now,
    // not only when a status answer arrives (logged-out pages get none).
    paintCtas();
  }
}
window.__stroma.register(initShop);
})();

/* ==== 40-grid.js ==== */
(function(){ 'use strict';
/* stroma-grid — product cards.
   Add-to-cart reuses the theme's shared quick-add, identical to
   /pages/running-shoes (handlers are theme-global, wired via inline onclick):
     desktop, single variant → plpAtc(this)            (adds straight to cart)
     desktop, multi variant  → ginzaATCOpen(this, uid)  (Select Size drawer → plpAtc per row)
     mobile, "+" corner      → openQuickView(this, handle) (theme quick-add sheet)
   This module handles what the shared helpers don't: smartCrop, pausing
   autoplay videos under reduced-motion, and closing the Select Size drawer.
   ginzaATCOpen only ever OPENS (its "close the others" step keys off theme
   class names this drawer doesn't use), so closing is owned here: opening one
   card's drawer closes every other card's, and an outside-click / ESC closes
   all. */
function initStromaGrid() {
  var sections = document.querySelectorAll('[data-st-grid]');
  if (!sections.length) return;

  /* Scroll-lock safety for the mobile quick-add path.
     The mobile "+" opens the theme quick-add sheet (openQuickView), whose size
     buttons call the theme's quickAddToCart. On add-success that helper
     unconditionally calls handlePopup('sticky-popup-atc', true) to dismiss the
     PDP sticky bar — but that popup does not exist on page.stroma. The theme's
     handlePopup adds body.overflow-hidden BEFORE it dereferences the element,
     then throws on the null access, so the matching remove never runs and the
     page is left scroll-locked after the cart popup closes. Make handlePopup
     null-safe here: closing a popup id that isn't on the page is a no-op, never
     a lingering scroll lock. Every real popup on this page has its element, so
     valid opens/closes are unaffected. */
  if (typeof window.handlePopup === 'function' && !window.handlePopup.__stromaNullSafe) {
    var themeHandlePopup = window.handlePopup;
    var safeHandlePopup = function (id) {
      if (!document.getElementById(id)) return false;
      return themeHandlePopup.apply(this, arguments);
    };
    safeHandlePopup.__stromaNullSafe = true;
    window.handlePopup = safeHandlePopup;
  }

  Array.prototype.forEach.call(sections, function (section) {
    Array.prototype.forEach.call(section.querySelectorAll('.st-grid__card-media img'), window.__stroma.smartCrop);

    /* Reduced motion: stop autoplaying feature videos; the poster/image stays. */
    if (window.__stroma.reducedMotion) {
      Array.prototype.forEach.call(section.querySelectorAll('video[autoplay]'), function (video) {
        video.removeAttribute('autoplay');
        try { video.pause(); } catch (e) { /* pause can throw pre-load; nothing to do */ }
      });
    }

    /* Close every card's drawer except `keepCard` (pass nothing to close all). */
    function closeDrawersExcept(keepCard) {
      Array.prototype.forEach.call(section.querySelectorAll('.st-grid__card'), function (card) {
        if (card === keepCard) return;
        var drawer = card.querySelector('.option-dropdown.active');
        if (drawer) drawer.classList.remove('active');
        var btn = card.querySelector('.st-grid__add.active');
        if (btn) btn.classList.remove('active');
      });
    }

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      // Inside an open drawer (a size row adds to cart): leave it open.
      if (e.target.closest('.st-grid__card .option-dropdown')) return;
      // Clicking a +Add opens its own drawer (ginzaATCOpen) — close the rest.
      // Clicking anywhere else closes all.
      var addBtn = e.target.closest('.st-grid__add');
      closeDrawersExcept(addBtn ? addBtn.closest('.st-grid__card') : null);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawersExcept(null);
    });
  });
}
window.__stroma.register(initStromaGrid);
})();

/* ==== 50-itinerary.js ==== */
(function(){ 'use strict';
/* 50 — STROMA Itinerary: flip-card carousel + download popup.
   Scroll-snap track drives both breakpoints; arrows/dashes/auto-advance all
   scroll the same container. Auto-advance (Figma dev annotation: 10s) pauses
   on hover, while a card is flipped, while the popup is open, and never runs
   under reduced motion. Email submit is an honest stub — no fake success. */
function initItinerary() {
  var sections = document.querySelectorAll('[data-st-itin]');
  if (!sections.length) return;

  sections.forEach(function (section) {
    var track = section.querySelector('[data-st-itin-track]');
    var cards = section.querySelectorAll('[data-st-itin-card]');
    if (!track || !cards.length) return;

    var prevBtn = section.querySelector('[data-st-itin-prev]');
    var nextBtn = section.querySelector('[data-st-itin-next]');
    var dots = section.querySelector('[data-st-itin-dots]');
    var popup = section.querySelector('[data-st-itin-popup]');
    var backdrop = section.querySelector('[data-st-itin-backdrop]');
    var popupTitle = section.querySelector('[data-st-itin-popup-title]');
    var popupClose = section.querySelector('[data-st-itin-popup-close]');
    var form = section.querySelector('[data-st-itin-form]');
    var status = section.querySelector('[data-st-itin-status]');

    var hovering = false;
    var popupOpen = false;
    var lastFocus = null;
    /* Carousel cursor is card-based, not scroll-position-based: on desktop a few
       cards share the viewport, so there are fewer scroll stops than cards and a
       position cursor could never reach the last ones. cardIdx is the active
       card (0..cards.length-1); goTo scrolls just far enough to reveal it. */
    var cardIdx = 0;
    var progScroll = false;

    /* ---- carousel geometry ---- */
    function step() {
      if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
      return cards[0].offsetWidth;
    }
    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }
    function positions() {
      return cards.length;
    }
    function current() {
      return cardIdx;
    }
    /* Scroll just far enough to reveal card `i` — the last cards clamp to the
       end (maxScroll), where they sit in the trailing visible slots. Sets the
       cursor and raises progScroll so the scroll-settle tick can't snap the
       cursor back to the leading card during this programmatic scroll. */
    function goTo(i) {
      var count = cards.length;
      cardIdx = ((i % count) + count) % count;
      progScroll = true;
      track.scrollTo({
        left: Math.min(cardIdx * step(), maxScroll()),
        behavior: window.__stroma.reducedMotion ? 'auto' : 'smooth'
      });
      window.setTimeout(function () { progScroll = false; }, 700);
      updateDots();
    }

    /* ---- dash pagination ---- */
    function buildDots() {
      if (!dots) return;
      dots.innerHTML = '';
      var count = positions();
      for (var i = 0; i < count; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'st-itin__dot';
        b.setAttribute('aria-label', 'Go to position ' + (i + 1) + ' of ' + count);
        b.dataset.stItinIndex = String(i);
        var dash = document.createElement('span');
        dash.className = 'st-itin__dash';
        b.appendChild(dash);
        b.addEventListener('click', function (e) {
          goTo(parseInt(e.currentTarget.dataset.stItinIndex, 10));
          activateCard(current());
        });
        dots.appendChild(b);
      }
      updateDots();
    }
    function updateDots() {
      if (!dots) return;
      var active = current();
      dots.querySelectorAll('.st-itin__dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === active);
      });
    }

    var scrollTick = false;
    track.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      window.requestAnimationFrame(function () {
        scrollTick = false;
        updateDots();
      });
    }, { passive: true });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildDots, 150);
    });

    /* ---- arrows (loop) ---- */
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current() - 1); activateCard(current()); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current() + 1); activateCard(current()); });

    /* ---- card flip ---- */
    function anyFlipped() {
      return !!section.querySelector('.st-itin__card.is-flipped');
    }
    section.querySelectorAll('[data-st-itin-flip]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-st-itin-card]');
        if (!card) return;
        card.classList.add('is-flipped');
        var back = card.querySelector('.st-itin__back');
        if (back) back.setAttribute('aria-hidden', 'false');
        var front = card.querySelector('.st-itin__front');
        if (front) front.setAttribute('aria-hidden', 'true');
        var close = card.querySelector('[data-st-itin-unflip]');
        if (close) close.focus({ preventScroll: true });
      });
    });
    section.querySelectorAll('[data-st-itin-unflip]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-st-itin-card]');
        if (!card) return;
        card.classList.remove('is-flipped');
        var back = card.querySelector('.st-itin__back');
        if (back) back.setAttribute('aria-hidden', 'true');
        var front = card.querySelector('.st-itin__front');
        if (front) front.setAttribute('aria-hidden', 'false');
        var view = card.querySelector('[data-st-itin-flip]');
        if (view) view.focus({ preventScroll: true });
      });
    });

    /* ---- download popup ---- */
    function onPopupKeydown(e) {
      if (e.key === 'Escape') closePopup();
    }
    var popupLeadTag = '';
    function openPopup(heading, leadTag) {
      if (!popup || !backdrop) return;
      popupLeadTag = leadTag || '';
      if (popupTitle && heading) {
        popupTitle.textContent = /\.$/.test(heading) ? heading : heading + '.';
      }
      if (status) {
        status.hidden = true;
        status.textContent = '';
      }
      lastFocus = document.activeElement;
      popup.hidden = false;
      backdrop.hidden = false;
      popupOpen = true;
      document.addEventListener('keydown', onPopupKeydown);
      var email = popup.querySelector('.st-itin__popup-email');
      if (email) email.focus({ preventScroll: true });
    }
    function closePopup() {
      if (!popup || !backdrop) return;
      popup.hidden = true;
      backdrop.hidden = true;
      popupOpen = false;
      document.removeEventListener('keydown', onPopupKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      lastFocus = null;
    }
    section.querySelectorAll('[data-st-itin-download]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openPopup(btn.dataset.itinHeading || '', btn.dataset.leadTag || '');
      });
    });
    // A saved download sign-up couldn't be finished after login: reopen that
    // card's popup with the message so the shopper can tap again.
    document.addEventListener('stroma:lead-resume-failed', function (e) {
      if (!e.detail || !e.detail.tag) return;
      var src = null;
      section.querySelectorAll('[data-st-itin-download]').forEach(function (b) {
        if (!src && b.dataset.leadTag === e.detail.tag) src = b;
      });
      if (!src) return;
      if (!popupOpen || popupLeadTag !== e.detail.tag) openPopup(src.dataset.itinHeading || '', e.detail.tag);
      if (status) { status.textContent = e.detail.message; status.hidden = false; }
    });
    if (popupClose) popupClose.addEventListener('click', closePopup);
    if (backdrop) backdrop.addEventListener('click', closePopup);

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var say = function (t) {
          if (status) { status.textContent = t; status.hidden = false; }
        };
        if (!popupLeadTag) {
          // A custom city with no derivable tag: honest, not fake-success.
          say('Sign-ups for this destination are not open yet.');
          return;
        }
        say('One moment…');
        window.__stroma.leadCapture(popupLeadTag, say);
      });
    }

    /* ---- auto-advance ---- */
    section.addEventListener('mouseenter', function () { hovering = true; });
    section.addEventListener('mouseleave', function () { hovering = false; });

    /* Hold each card until its shoe video finishes, then move to the next
       (wrapping). A fallback timer guarantees the carousel never stalls if a
       card has no video or the clip never fires `ended`. Hover / open popup /
       flipped card / hidden tab / reduced motion all pause it. */
    var fallbackMs = parseInt(section.dataset.stItinAutoplay || '0', 10) || 10000;
    var itinVideos = [];
    Array.prototype.forEach.call(cards, function (card) { itinVideos.push(card.querySelector('video')); });
    var activeVid = -1;
    var itinFallback = null;
    var itinInView = false;
    function canAdvance() {
      return itinInView && !window.__stroma.reducedMotion && !hovering && !popupOpen && !anyFlipped() && !document.hidden;
    }
    function advance() {
      var n = current() + 1;
      if (n >= positions()) n = 0;
      goTo(n);
      // Start the next clip the instant we advance, rather than waiting for
      // the scroll-settle tick — keeps the playing video and the visible card
      // in lockstep.
      activateCard(n);
    }
    function armFallback(ms) {
      if (itinFallback) clearTimeout(itinFallback);
      itinFallback = setTimeout(function () { if (canAdvance()) advance(); }, ms);
    }
    // Serial playback: only the active card's video ever runs. Activating a
    // card pauses and rewinds EVERY other video, then plays this one from the
    // start. The next clip begins only when the carousel lands on its card —
    // auto-advance on `ended`, or manual navigation — never all at once.
    function activateCard(i) {
      if (i === activeVid) return;
      activeVid = i;
      Array.prototype.forEach.call(itinVideos, function (vid, j) {
        if (!vid || j === i) return;
        try { vid.pause(); vid.currentTime = 0; } catch (e) {}
      });
      var v = itinVideos[i];
      if (v) {
        try { v.currentTime = 0; } catch (e) {}
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        armFallback(isFinite(v.duration) && v.duration > 0 ? v.duration * 1000 + 600 : fallbackMs);
      } else {
        armFallback(fallbackMs);
      }
    }
    // Neutralise the markup's autoplay/loop so the browser can't run every clip
    // in parallel on load — JS owns which single clip plays. `ended` advances
    // the carousel only when THIS video is the one currently in view.
    Array.prototype.forEach.call(itinVideos, function (v) {
      if (!v) return;
      v.removeAttribute('autoplay');
      v.removeAttribute('loop');
      try { v.pause(); } catch (e) {}
      v.addEventListener('ended', function () {
        if (canAdvance() && itinVideos[current()] === v) advance();
      });
    });
    if (!window.__stroma.reducedMotion) {
      var itinSettle = null;
      track.addEventListener('scroll', function () {
        if (itinSettle) clearTimeout(itinSettle);
        itinSettle = setTimeout(function () {
          // A programmatic scroll (goTo) owns the cursor; only a user scroll
          // moves it. Map scroll PROGRESS across the whole range onto the full
          // card list so a manual slide reaches every card: on desktop the last
          // cards never reach the leading edge, so a leading-card mapping could
          // never select them. maxScroll==0 (all cards fit) → first card.
          if (!itinInView || progScroll) return;
          var ms = maxScroll();
          cardIdx = ms > 0 ? Math.min(Math.round(track.scrollLeft / ms * (cards.length - 1)), cards.length - 1) : 0;
          updateDots();
          activateCard(current());
        }, 140);
      }, { passive: true });
      // Only play/advance while the carousel is on-screen: a below-the-fold
      // section must not stream video, so the mobile network stays idle for
      // first paint and load-time measurement. Entering view starts the active
      // card; leaving pauses it and forces re-activation on return.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            itinInView = e.isIntersecting;
            if (e.isIntersecting) {
              activateCard(current());
            } else {
              var cv = itinVideos[current()];
              if (cv) { try { cv.pause(); } catch (err) {} }
              if (itinFallback) { clearTimeout(itinFallback); itinFallback = null; }
              activeVid = -1;
            }
          });
        }, { rootMargin: '150px 0px', threshold: 0.2 }).observe(section);
      } else {
        itinInView = true;
        activateCard(current());
      }
    }

    buildDots();
  });
}
window.__stroma.register(initItinerary);
})();

/* ==== 60-features.js ==== */
(function(){ 'use strict';
/* 60 — STROMA features auto-slider engine.
   Crossfade between 3 slide states; the active nav label's underline is the
   autoplay progress loader (CSS animation, duration set here so the loader
   and the advance timer share one number). Hover pauses both (timer keeps
   its remaining time; the CSS animation is paused via .is-paused). Clicking
   a label jumps to that slide and restarts the loader at full duration.
   Reduced motion: no autoplay, no loader animation — nav still works and
   the active underline renders statically full-width (CSS). */
function initStFeatures() {
  var roots = document.querySelectorAll('[data-st-feat]');
  for (var r = 0; r < roots.length; r++) {
    (function (root) {
      var slides = root.querySelectorAll('[data-st-feat-slide]');
      var buttons = root.querySelectorAll('[data-st-feat-goto]');
      if (!slides.length || slides.length !== buttons.length) return;

      /* Slide media are lazy and the autoplay runs regardless of scroll
         position, so a slide can become active before its lazy image has
         loaded — the first loop flashes blank, the second is cached. Warm
         the current breakpoint's slide images once the section is ~800px
         away so they are decoded before the slider cycles to them. Only the
         displayed breakpoint is warmed (offsetParent is null for the
         display:none copy) so the wrong breakpoint never downloads. */
      if ('IntersectionObserver' in window) {
        var warmIo = new IntersectionObserver(function (entries) {
          for (var e = 0; e < entries.length; e++) {
            if (!entries[e].isIntersecting) continue;
            var imgs = root.querySelectorAll('img.st-feat__bgimg');
            for (var k = 0; k < imgs.length; k++) {
              if (imgs[k].offsetParent !== null) imgs[k].loading = 'eager';
            }
            warmIo.disconnect();
            break;
          }
        }, { rootMargin: '800px 0px' });
        warmIo.observe(root);
      }

      var reduced = window.__stroma && window.__stroma.reducedMotion;
      var duration = (parseInt(root.getAttribute('data-st-feat-autoplay'), 10) || 10) * 1000;
      var idx = 0;
      var timerId = null;
      var startedAt = 0;
      var remaining = duration;
      var hovered = false;

      function render() {
        for (var i = 0; i < slides.length; i++) {
          var active = i === idx;
          slides[i].classList.toggle('is-active', active);
          slides[i].setAttribute('aria-hidden', active ? 'false' : 'true');
          buttons[i].classList.toggle('is-active', active);
          if (active) buttons[i].setAttribute('aria-current', 'true');
          else buttons[i].removeAttribute('aria-current');
        }
      }

      function restartLoader() {
        for (var i = 0; i < buttons.length; i++) {
          var bar = buttons[i].querySelector('.st-feat__navprogress');
          if (!bar) continue;
          bar.classList.remove('is-run');
          if (i === idx && !reduced) {
            void bar.offsetWidth; /* reflow so the animation restarts */
            bar.style.animationDuration = duration + 'ms';
            bar.classList.add('is-run');
          }
        }
      }

      function clearTimer() {
        if (timerId) {
          clearTimeout(timerId);
          timerId = null;
        }
      }

      function schedule(ms) {
        clearTimer();
        if (reduced) return;
        startedAt = Date.now();
        remaining = ms;
        timerId = setTimeout(function () {
          goTo((idx + 1) % slides.length);
        }, ms);
      }

      function goTo(i) {
        idx = i;
        render();
        restartLoader();
        if (hovered) remaining = duration; /* resumes at full length on leave */
        else schedule(duration);
      }

      for (var b = 0; b < buttons.length; b++) {
        (function (i) {
          buttons[i].addEventListener('click', function () {
            goTo(i);
          });
        })(b);
      }

      root.addEventListener('mouseenter', function () {
        hovered = true;
        if (reduced) return;
        if (timerId) {
          clearTimer();
          remaining -= Date.now() - startedAt;
          if (remaining < 250) remaining = 250;
        }
        root.classList.add('is-paused');
      });
      root.addEventListener('mouseleave', function () {
        hovered = false;
        if (reduced) return;
        root.classList.remove('is-paused');
        schedule(remaining);
      });

      render();

      /* Autoplay runs only while the section is on-screen. Cycling slides,
         running the timer, and animating the progress bar for a section nobody
         is viewing is wasted main-thread work — and it would otherwise leave the
         slider mid-cycle by the time the viewer scrolls in. Pause off-screen and
         (re)start the current slide fresh on entry; the hover pause still
         applies on top of this. No IntersectionObserver → start immediately. */
      function playGate(on) {
        if (reduced) return;
        if (on) {
          root.classList.remove('is-paused');
          if (!hovered) { restartLoader(); schedule(duration); }
        } else {
          clearTimer();
          root.classList.add('is-paused');
        }
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          playGate(entries[entries.length - 1].isIntersecting);
        }).observe(root);
      } else {
        restartLoader();
        schedule(duration);
      }
    })(roots[r]);
  }
}
/* Spec-table rows are selectable: the chosen row gets the design's selected
   state, the annotation under the shoe mirrors its label/value, and a row
   authored with its own image swaps the render (the design ships a single
   shoe render, so image swaps only happen once per-row assets exist). */
function initStSpecRows() {
  var tables = document.querySelectorAll('.st-feat__table');
  for (var t = 0; t < tables.length; t++) {
    (function (table) {
      var rows = table.querySelectorAll('[data-st-spec-row]');
      if (!rows.length) return;
      var slide = table.closest('.st-feat__slide--specs');
      var annoLabel = slide && slide.querySelector('.st-feat__anno-label');
      var annoValue = slide && slide.querySelector('.st-feat__anno-value');
      var mainImg = slide && slide.querySelector('.st-feat__shoe-main');
      var zoomImg = slide && slide.querySelector('.st-feat__zoom-img');

      function select(row) {
        for (var i = 0; i < rows.length; i++) {
          var on = rows[i] === row;
          rows[i].classList.toggle('is-selected', on);
          rows[i].setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        var label = row.querySelector('.st-feat__spec-labeltext');
        var value = row.querySelector('.st-feat__spec-valuetext');
        if (annoLabel && label) annoLabel.textContent = label.textContent;
        if (annoValue && value) annoValue.textContent = value.textContent;
        var img = row.getAttribute('data-img');
        if (img) {
          if (mainImg) { mainImg.removeAttribute('srcset'); mainImg.src = img; }
          if (zoomImg) { zoomImg.removeAttribute('srcset'); zoomImg.src = img; }
        }
      }

      for (var i = 0; i < rows.length; i++) {
        (function (row) {
          row.addEventListener('click', function () { select(row); });
          row.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(row); }
          });
        })(rows[i]);
      }
    })(tables[t]);
  }
}

window.__stroma.register(initStFeatures);
window.__stroma.register(initStSpecRows);
})();

/* ==== 70-landing.js ==== */
(function(){ 'use strict';
/* 70 — STROMA landing: FAQ accordion + email-capture honest stub.
   Accordion: button toggles .is-open on its item (CSS grid-rows animates
   height; chevron rotation is CSS). Email form is NOT connected — submit
   shows the inline "not connected" note and warns to console; it never
   fakes success. */
function initStLanding() {
  var roots = document.querySelectorAll('[data-st-land]');
  for (var r = 0; r < roots.length; r++) {
    (function (root) {
      var buttons = root.querySelectorAll('[data-st-land-q]');
      for (var i = 0; i < buttons.length; i++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            var expanded = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
            var item = btn.parentNode;
            if (item) item.classList.toggle('is-open', !expanded);
          });
        })(buttons[i]);
      }

      var form = root.querySelector('[data-st-land-form]');
      if (form) {
        var landBtn = form.querySelector('.st-land__notify');
        var landLabel = landBtn && landBtn.querySelector('.st-label');
        var landTag = form.dataset.leadTag || 'notifyme-stromaLaunch';
        var custId = (form.dataset.customerId || '').trim();
        var DONE_KEY = 'stroma:lead:done:' + landTag;
        // The backend has no status GET, so the on-list confirmation can't be
        // re-derived from the server on load. Persist it here — written only
        // after a real server 200 — so a reload keeps the confirmation without a
        // re-tap. Keyed to the logged-in customer id so a different login on the
        // same browser (or a logged-out visitor) never inherits a stale one.
        var persistOnList = function () {
          if (!custId) return;
          try { localStorage.setItem(DONE_KEY, custId); } catch (err) { /* storage blocked */ }
        };
        // On a successful capture the button itself becomes the confirmation —
        // flip its label, disable it, and drop the separate note.
        var markOnList = function () {
          if (landLabel) landLabel.textContent = "You're on the list";
          if (landBtn) { landBtn.disabled = true; landBtn.classList.add('is-done'); }
          var note = form.querySelector('[data-st-land-note]');
          if (note) { note.hidden = true; note.textContent = ''; }
          persistOnList();
        };
        form.addEventListener('submit', function (ev) {
          ev.preventDefault();
          var note = form.querySelector('[data-st-land-note]');
          var say = function (t) {
            if (note) { note.textContent = t; note.hidden = false; }
          };
          say('One moment…');
          window.__stroma.leadCapture(landTag, say).then(function (ok) {
            if (ok) markOnList();
          });
        });
        // Post-login reload: the silent lead resume fires this once it has
        // confirmed the tag server-side. Scroll the signup into view and flip
        // the button so the shopper sees they're on the list — no second tap.
        document.addEventListener('stroma:lead-resumed', function (e) {
          if (e.detail && e.detail.tag === landTag) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
            markOnList();
          }
        });
        // The saved sign-up couldn't be finished: say so under the button.
        document.addEventListener('stroma:lead-resume-failed', function (e) {
          if (!e.detail || e.detail.tag !== landTag || (landBtn && landBtn.disabled)) return;
          var note = form.querySelector('[data-st-land-note]');
          if (note) { note.textContent = e.detail.message; note.hidden = false; }
        });
        // Reload after a prior capture on this browser: restore the confirmation
        // so an already-registered shopper isn't asked to tap again.
        try {
          if (custId && localStorage.getItem(DONE_KEY) === custId) markOnList();
        } catch (err) { /* storage blocked: shopper retaps */ }
      }
    })(roots[r]);
  }
}
window.__stroma.register(initStLanding);

/* Perf: a section's video streams only while it is on-screen. Autoplaying every
   video at once saturates a mobile connection — the network never goes idle,
   which stalls real first paint and makes the page immeasurable to load-time
   tools. Each non-itinerary video is stripped of autoplay and set preload=none;
   an IntersectionObserver plays it when it scrolls into view and pauses it when
   it leaves, so off-screen and hidden-breakpoint videos never download. The
   itinerary runs its own serial playback (initItinerary) and is left alone. */
function initLazyVideos() {
  var vids = [];
  Array.prototype.forEach.call(document.querySelectorAll('video'), function (v) {
    if (v.closest && v.closest('[data-st-itin]')) return; // itinerary self-manages
    // Hero/benches are managed here too: a display:none hidden-breakpoint copy
    // never intersects, so its video is never fetched. (When these autoplayed
    // natively the wrong-breakpoint copy still downloaded — ~5MB on mobile.)
    v.removeAttribute('autoplay');
    try { v.pause(); } catch (e) {}
    v.preload = 'none';
    vids.push(v);
  });
  if (!vids.length) return;
  if (window.__stroma && window.__stroma.reducedMotion) return; // leave paused
  if (!('IntersectionObserver' in window)) {
    vids.forEach(function (v) { v.preload = 'metadata'; var p = v.play(); if (p && p.catch) p.catch(function () {}); });
    return;
  }
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target;
      if (e.isIntersecting) {
        if (v.preload !== 'auto') v.preload = 'auto';
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      } else {
        try { v.pause(); } catch (err) {}
      }
    });
  }, { rootMargin: '800px 0px', threshold: 0.15 });
  vids.forEach(function (v) { io.observe(v); });
}
window.__stroma.register(initLazyVideos);
})();







